import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateLocation } from "../../../../redux/locationReducer";
import {
  validateCustomerData,
  ValidationError,
} from "../../../../utils/orderUtils";
import ErrorSummary from "../../../../components/ErrorSummary/ErrorSummary";
import styles from "./CustomerData.module.css";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface CustomerDataProps {
  onConfirm: () => void;
  onChange: (data: any) => void;
  onEdit: () => void;
}

const CustomerData: React.FC<CustomerDataProps> = ({
  onConfirm,
  onChange,
  onEdit,
}) => {
  const dispatch = useDispatch();
  const location = useSelector((state: any) => state.location);

  // Form refs for scrolling to errors
  const formRefs = useRef<
    Record<string, HTMLInputElement | HTMLSelectElement | null>
  >({
    firstName: null,
    lastName: null,
    phone: null,
    documentType: null,
    documentNumber: null,
    receiverName: null,
    receiverDocumentType: null,
    receiverDocumentNumber: null,
    receiverPhone: null,
    ruc: null,
    razonSocial: null,
    direccionFiscal: null,
  });

  const sectionRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    firstName: location.name || "",
    lastName: location.lastname || "",
    phone: location.phoneNumber || "",
    documentType: "",
    documentNumber: "",
    receiverDifferent: false,
    receiverDocumentType: "",
    receiverDocumentNumber: "",
    receiverName: "",
    receiverPhone: "",
    invoiceRequired: false,
    ruc: "",
    razonSocial: "",
    direccionFiscal: "",
    receiptType: "boleta", // Default value
  });

  const [formErrors, setFormErrors] = useState<Record<string, ValidationError>>(
    {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target as
      | HTMLInputElement
      | HTMLSelectElement;
    const checked = (e.target as HTMLInputElement).checked;
    const updatedFormData = {
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    };

    // Update receiptType based on invoiceRequired checkbox
    if (name === "invoiceRequired") {
      updatedFormData.receiptType = checked ? "factura" : "boleta";
    }

    setFormData(updatedFormData);

    // Clear error when field is edited
    if (formErrors[name]) {
      const newErrors = { ...formErrors };
      delete newErrors[name];
      setFormErrors(newErrors);
    }
  };

  const scrollToField = (fieldId: string) => {
    const element = formRefs.current[fieldId];
    if (element) {
      element.focus();
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleConfirm = () => {
    setIsSubmitting(true);
    const validationResult = validateCustomerData(formData);

    if (validationResult.hasErrors) {
      setFormErrors(validationResult.errors);
      setShowErrors(true); // Make sure this is set to true to display error summary
      setIsSubmitting(false);

      // Scroll to error summary
      if (sectionRef.current) {
        sectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      // Set focus on first field with error after a short delay
      if (validationResult.firstErrorField) {
        setTimeout(() => {
          scrollToField(validationResult.firstErrorField!);
        }, 300);
      }

      return;
    }

    setFormErrors({});
    setShowErrors(false);
    setIsConfirmed(true);
    onChange(formData);
    dispatch(
      updateLocation({
        name: formData.firstName,
        lastname: formData.lastName,
        phoneNumber: formData.phone,
      })
    );
    onConfirm();
    setIsSubmitting(false);
  };

  const handleEdit = () => {
    setIsConfirmed(false);
    onEdit();
  };

  return (
    <div className={styles.card} ref={sectionRef}>
      <div className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Paso 2 / 3: Info de contacto</h2>
        {isConfirmed && <CheckCircleOutlineIcon />}
      </div>
      <div className={styles.cardContent}>
        {!isConfirmed ? (
          <>
            {/* Error Summary */}
            {showErrors && Object.keys(formErrors).length > 0 && (
              <ErrorSummary errors={formErrors} onErrorClick={scrollToField} />
            )}

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="firstName">
                  Nombres <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`${styles.input} ${
                      formErrors.firstName ? styles.errorInput : ""
                    }`}
                    aria-invalid={formErrors.firstName ? "true" : "false"}
                    aria-describedby={
                      formErrors.firstName ? "firstName-error" : undefined
                    }
                    ref={(el) => (formRefs.current.firstName = el)}
                  />
                  {formErrors.firstName && (
                    <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  )}
                </div>
                {formErrors.firstName && (
                  <span
                    id="firstName-error"
                    className={styles.errorMessage}
                    role="alert"
                  >
                    {formErrors.firstName.message}
                  </span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="lastName">
                  Apellidos <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`${styles.input} ${
                      formErrors.lastName ? styles.errorInput : ""
                    }`}
                    aria-invalid={formErrors.lastName ? "true" : "false"}
                    aria-describedby={
                      formErrors.lastName ? "lastName-error" : undefined
                    }
                    ref={(el) => (formRefs.current.lastName = el)}
                  />
                  {formErrors.lastName && (
                    <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  )}
                </div>
                {formErrors.lastName && (
                  <span
                    id="lastName-error"
                    className={styles.errorMessage}
                    role="alert"
                  >
                    {formErrors.lastName.message}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="documentType">
                  Tipo de Documento <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <select
                    id="documentType"
                    name="documentType"
                    value={formData.documentType}
                    onChange={handleChange}
                    className={`${styles.select} ${
                      formErrors.documentType ? styles.errorInput : ""
                    }`}
                    aria-invalid={formErrors.documentType ? "true" : "false"}
                    aria-describedby={
                      formErrors.documentType ? "documentType-error" : undefined
                    }
                    ref={(el) => (formRefs.current.documentType = el)}
                  >
                    <option value="">Seleccione un tipo de documento</option>
                    <option value="DNI">DNI</option>
                    <option value="Pasaporte">Pasaporte</option>
                    <option value="Carnet de Extranjería">
                      Carnet de Extranjería
                    </option>
                  </select>
                  {formErrors.documentType && (
                    <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  )}
                </div>
                {formErrors.documentType && (
                  <span
                    id="documentType-error"
                    className={styles.errorMessage}
                    role="alert"
                  >
                    {formErrors.documentType.message}
                  </span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="documentNumber">
                  Número de Documento <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    id="documentNumber"
                    name="documentNumber"
                    value={formData.documentNumber}
                    onChange={handleChange}
                    className={`${styles.input} ${
                      formErrors.documentNumber ? styles.errorInput : ""
                    }`}
                    aria-invalid={formErrors.documentNumber ? "true" : "false"}
                    aria-describedby={
                      formErrors.documentNumber
                        ? "documentNumber-error"
                        : undefined
                    }
                    ref={(el) => (formRefs.current.documentNumber = el)}
                  />
                  {formErrors.documentNumber && (
                    <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  )}
                </div>
                {formErrors.documentNumber && (
                  <span
                    id="documentNumber-error"
                    className={styles.errorMessage}
                    role="alert"
                  >
                    {formErrors.documentNumber.message}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="phone">
                  Celular <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`${styles.input} ${
                      formErrors.phone ? styles.errorInput : ""
                    }`}
                    aria-invalid={formErrors.phone ? "true" : "false"}
                    aria-describedby={
                      formErrors.phone ? "phone-error" : undefined
                    }
                    ref={(el) => (formRefs.current.phone = el)}
                  />
                  {formErrors.phone && (
                    <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  )}
                </div>
                {formErrors.phone && (
                  <span
                    id="phone-error"
                    className={styles.errorMessage}
                    role="alert"
                  >
                    {formErrors.phone.message}
                  </span>
                )}
              </div>
            </div>
            <div className={styles.divider}></div>
            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="receiverDifferent"
                  checked={formData.receiverDifferent}
                  onChange={handleChange}
                />
                <span>El pedido lo recibirá alguien más</span>
              </label>

              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="invoiceRequired"
                  checked={formData.invoiceRequired}
                  onChange={handleChange}
                />
                <span>Deseo Factura</span>
              </label>
            </div>

            {formData.receiverDifferent && (
              <div className={styles.extraSection}>
                <h3>Datos de quien recibe</h3>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="receiverDocumentType">
                      Tipo de Documento{" "}
                      <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputWrapper}>
                      <select
                        id="receiverDocumentType"
                        name="receiverDocumentType"
                        value={formData.receiverDocumentType}
                        onChange={handleChange}
                        className={`${styles.select} ${
                          formErrors.receiverDocumentType
                            ? styles.errorInput
                            : ""
                        }`}
                        aria-invalid={
                          formErrors.receiverDocumentType ? "true" : "false"
                        }
                        aria-describedby={
                          formErrors.receiverDocumentType
                            ? "receiverDocumentType-error"
                            : undefined
                        }
                        ref={(el) =>
                          (formRefs.current.receiverDocumentType = el)
                        }
                      >
                        <option value="">
                          Seleccione un tipo de documento
                        </option>
                        <option value="DNI">DNI</option>
                        <option value="Pasaporte">Pasaporte</option>
                        <option value="Carnet de Extranjería">
                          Carnet de Extranjería
                        </option>
                      </select>
                      {formErrors.receiverDocumentType && (
                        <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                      )}
                    </div>
                    {formErrors.receiverDocumentType && (
                      <span
                        id="receiverDocumentType-error"
                        className={styles.errorMessage}
                        role="alert"
                      >
                        {formErrors.receiverDocumentType.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="receiverDocumentNumber">
                      Número de Documento{" "}
                      <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputWrapper}>
                      <input
                        id="receiverDocumentNumber"
                        name="receiverDocumentNumber"
                        value={formData.receiverDocumentNumber}
                        onChange={handleChange}
                        className={`${styles.input} ${
                          formErrors.receiverDocumentNumber
                            ? styles.errorInput
                            : ""
                        }`}
                        aria-invalid={
                          formErrors.receiverDocumentNumber ? "true" : "false"
                        }
                        aria-describedby={
                          formErrors.receiverDocumentNumber
                            ? "receiverDocumentNumber-error"
                            : undefined
                        }
                        ref={(el) =>
                          (formRefs.current.receiverDocumentNumber = el)
                        }
                      />
                      {formErrors.receiverDocumentNumber && (
                        <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                      )}
                    </div>
                    {formErrors.receiverDocumentNumber && (
                      <span
                        id="receiverDocumentNumber-error"
                        className={styles.errorMessage}
                        role="alert"
                      >
                        {formErrors.receiverDocumentNumber.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="receiverPhone">
                      Celular <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputWrapper}>
                      <input
                        id="receiverPhone"
                        name="receiverPhone"
                        value={formData.receiverPhone}
                        onChange={handleChange}
                        className={`${styles.input} ${
                          formErrors.receiverPhone ? styles.errorInput : ""
                        }`}
                        aria-invalid={
                          formErrors.receiverPhone ? "true" : "false"
                        }
                        aria-describedby={
                          formErrors.receiverPhone
                            ? "receiverPhone-error"
                            : undefined
                        }
                        ref={(el) => (formRefs.current.receiverPhone = el)}
                      />
                      {formErrors.receiverPhone && (
                        <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                      )}
                    </div>
                    {formErrors.receiverPhone && (
                      <span
                        id="receiverPhone-error"
                        className={styles.errorMessage}
                        role="alert"
                      >
                        {formErrors.receiverPhone.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="receiverName">
                      Nombre <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputWrapper}>
                      <input
                        id="receiverName"
                        name="receiverName"
                        value={formData.receiverName}
                        onChange={handleChange}
                        className={`${styles.input} ${
                          formErrors.receiverName ? styles.errorInput : ""
                        }`}
                        aria-invalid={
                          formErrors.receiverName ? "true" : "false"
                        }
                        aria-describedby={
                          formErrors.receiverName
                            ? "receiverName-error"
                            : undefined
                        }
                        ref={(el) => (formRefs.current.receiverName = el)}
                      />
                      {formErrors.receiverName && (
                        <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                      )}
                    </div>
                    {formErrors.receiverName && (
                      <span
                        id="receiverName-error"
                        className={styles.errorMessage}
                        role="alert"
                      >
                        {formErrors.receiverName.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {formData.invoiceRequired && (
              <div className={styles.extraSection}>
                <h3>Datos de la Factura</h3>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="ruc">
                      RUC <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputWrapper}>
                      <input
                        id="ruc"
                        name="ruc"
                        value={formData.ruc}
                        onChange={handleChange}
                        className={`${styles.input} ${
                          formErrors.ruc ? styles.errorInput : ""
                        }`}
                        aria-invalid={formErrors.ruc ? "true" : "false"}
                        aria-describedby={
                          formErrors.ruc ? "ruc-error" : undefined
                        }
                        ref={(el) => (formRefs.current.ruc = el)}
                      />
                      {formErrors.ruc && (
                        <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                      )}
                    </div>
                    {formErrors.ruc && (
                      <span
                        id="ruc-error"
                        className={styles.errorMessage}
                        role="alert"
                      >
                        {formErrors.ruc.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="razonSocial">
                      Razón Social <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputWrapper}>
                      <input
                        id="razonSocial"
                        name="razonSocial"
                        value={formData.razonSocial}
                        onChange={handleChange}
                        className={`${styles.input} ${
                          formErrors.razonSocial ? styles.errorInput : ""
                        }`}
                        aria-invalid={formErrors.razonSocial ? "true" : "false"}
                        aria-describedby={
                          formErrors.razonSocial
                            ? "razonSocial-error"
                            : undefined
                        }
                        ref={(el) => (formRefs.current.razonSocial = el)}
                      />
                      {formErrors.razonSocial && (
                        <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                      )}
                    </div>
                    {formErrors.razonSocial && (
                      <span
                        id="razonSocial-error"
                        className={styles.errorMessage}
                        role="alert"
                      >
                        {formErrors.razonSocial.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="direccionFiscal">
                    Dirección Fiscal <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.inputWrapper}>
                    <input
                      id="direccionFiscal"
                      name="direccionFiscal"
                      value={formData.direccionFiscal}
                      onChange={handleChange}
                      className={`${styles.input} ${
                        formErrors.direccionFiscal ? styles.errorInput : ""
                      }`}
                      aria-invalid={
                        formErrors.direccionFiscal ? "true" : "false"
                      }
                      aria-describedby={
                        formErrors.direccionFiscal
                          ? "direccionFiscal-error"
                          : undefined
                      }
                      ref={(el) => (formRefs.current.direccionFiscal = el)}
                    />
                    {formErrors.direccionFiscal && (
                      <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                    )}
                  </div>
                  {formErrors.direccionFiscal && (
                    <span
                      id="direccionFiscal-error"
                      className={styles.errorMessage}
                      role="alert"
                    >
                      {formErrors.direccionFiscal.message}
                    </span>
                  )}
                </div>
              </div>
            )}

            {Object.keys(formErrors).length > 0 && showErrors && (
              <div className={styles.fixErrorsContainer}>
                <button
                  type="button"
                  className={styles.fixErrorsButton}
                  onClick={() => scrollToField(Object.keys(formErrors)[0])}
                >
                  <ErrorOutlineIcon className={styles.fixErrorsIcon} />
                  Corregir errores
                </button>
              </div>
            )}

            <button
              className={styles.confirmButton}
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Procesando..." : "Confirmar"}
            </button>
          </>
        ) : (
          <div className={styles.cardSummary}>
            <p>
              <strong>
                {formData.firstName} {formData.lastName}
              </strong>
            </p>
            <p>
              {formData.documentType}:{" "}
              <strong>{formData.documentNumber}</strong>
            </p>
            <p>
              Celular de contacto: <strong>{formData.phone}</strong>
            </p>

            {formData.receiverDifferent && (
              <>
                <h3>Datos de quien recibe</h3>
                <p>
                  <strong>{formData.receiverName}</strong>
                </p>
                <p>
                  {formData.receiverDocumentType}:{" "}
                  <strong>{formData.receiverDocumentNumber}</strong>
                </p>
                <p>
                  Celular: <strong>{formData.receiverPhone}</strong>
                </p>
              </>
            )}
            {formData.invoiceRequired && (
              <>
                <h3>Datos de la Factura</h3>
                <p>
                  RUC: <strong>{formData.ruc}</strong>
                </p>
                <p>
                  Razón Social: <strong>{formData.razonSocial}</strong>
                </p>
                <p>
                  Dirección Fiscal: <strong>{formData.direccionFiscal}</strong>
                </p>
              </>
            )}
            <button className={styles.editButton} onClick={handleEdit}>
              Editar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerData;
